import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DRUG_OPTION_SCHEMA } from "components/DynamicSwitchGroup/DrugOptionBox/DrugOptionBoxCompact";
import Spinner from "components/Spinner";
import { Formik } from "formik";
import { Button } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { getDynamicSwitchGroup } from "views/Cat/DynamicSwitchGroupDetails/DynamicSwitchGroupDetails.actions";
import CheckField from "../../../commons/fields/CheckField";
import DescriptionField from "../../../commons/fields/DescriptionField";
import AllDrugList from "../AllDrugs/AllDrugList";
import { AgeFields } from "./AgeFields";
import "./NewDrug.css";
import NewDrugRestrictedItemTypeField from "./NewDrugRestrictedItemTypeField";
import TagField from "./TagField";
import withRouter from "utils/withRouter";

const CHOOSE_DEFAULT_DRUG = "Choose a drug from the following list";
const DRUG_VARIABLES = {
  itemType: "",
  instructions: "",
  faxDisplayName: "",
  priority: "",
  daysSupply: "",
  daysSupplyError: null,
  ageLmtReqd: false,
  minAge: "",
  maxAge: "",
  ageError: null,
  offMarketStrengthsExist: false,
};

const NewDrug = (props) => {
  const dispatch = useDispatch();
  const history = props.history;

  const workingCodexVersionId = useSelector((state) =>
    state.codexVersion ? state.codexVersion.codexVersionId : null
  );
  const dynamicSwitchGroup = useSelector((state) =>
    state.dynamicSwitchGroupDetails
      ? state.dynamicSwitchGroupDetails.dynamicSwitchGroup
      : null
  );
  const dynamicSwitchGroupId = useSelector((state) =>
    state.dynamicSwitchGroupDetails?.dynamicSwitchGroup
      ? state.dynamicSwitchGroupDetails.dynamicSwitchGroup.dynamicSwitchGroupId
      : null
  );

  const [selected, setSelected] = useState({});
  const [drugValue, setDrugValue] = useState({ ...DRUG_VARIABLES });
  const [isLoadingValidCases, setIsLoadingValidCases] = useState(false);
  const [shouldAddMoreDrug, setShouldAddMoreDrug] = useState(false);
  const [daysSupplyError, setDaysSupplyError] = useState(null);
  const [ageError, setAgeError] = useState(null);
  const [toggleResetTableSelection, setToggleResetTableSelection] = useState(false);

  if (!dynamicSwitchGroup) {
    history.goBack();
  }

  const refreshList = useCallback(() => {
    if (dynamicSwitchGroupId) {
      dispatch(getDynamicSwitchGroup(dynamicSwitchGroupId));
    }
  }, [dispatch, dynamicSwitchGroupId]);

  const returnInitailState = useCallback(() => {
    let toggleResetTableSelectionUpdated = toggleResetTableSelection
      ? false
      : true;
      toggleResetTableSelectionUpdated =
      Object.keys(selected ? selected : {}).length == 0
        ? !toggleResetTableSelectionUpdated
        : toggleResetTableSelectionUpdated;
    return {
      selected: {},
      toggleResetTableSelection: toggleResetTableSelectionUpdated,
      drugValue: { ...DRUG_VARIABLES },
      isLoadingValidCases: false,
      shouldAddMoreDrug,
    };
  }, [shouldAddMoreDrug]);

  const onSelect = (newSelected) => {
    if (!newSelected) {
      const reset = returnInitailState();
      setSelected(reset.selected);
      setDrugValue(reset.drugValue);
      setIsLoadingValidCases(reset.isLoadingValidCases);
      setToggleResetTableSelection(reset.toggleResetTableSelection);
      return;
    }
    const drugName = newSelected?.medName;
    let nextDrugValue =
      newSelected.drugDsoId !== selected.drugDsoId
        ? { ...DRUG_VARIABLES }
        : { ...drugValue };
    nextDrugValue.faxDisplayName = drugName;
    nextDrugValue.instructions = "Use as Directed";
    nextDrugValue.daysSupply = "30;60;90";
    nextDrugValue.offMarketStrengthsExist =
      newSelected?.offMarketStrengths && newSelected.offMarketStrengths !== ""
        ? true
        : false;
    if (nextDrugValue.offMarketStrengthsExist) {
      showNotification({
        title: "Warning!",
        message: "The selected drug contains some off-market strengths",
        position: "tr",
        type: "warning",
      });
    }
    setSelected(newSelected);
    setDrugValue(nextDrugValue);
  };

  const addDays = (days) => {
    let lastDayList = days ? days.split(";") : [];
    let error = null;
    for (let i = 0; i < lastDayList.length; i++) {
      let lastDay = lastDayList[i];
      if (!lastDay) {
        if (i != lastDayList.length - 1) {
          error = "Days supply cannot be empty";
          break;
        } else {
          continue;
        }
      } else if (lastDayList.length > 5) {
        error = "You cannot add more than 5 supplies!";
        break;
      }
      if (lastDayList.indexOf(lastDay) < i) {
        error = "You cannot add duplicate value!";
        break;
      } else if (lastDay < 15 || lastDay > 365) {
        error = "You can add days from 15 to 365 only!";
        break;
      }
    }
    setDaysSupplyError(error);
    return days;
  };

  const getDrug = () => {
    let drug = selected?.drugDsoId
      ? `${selected.medName} - ${selected.gpiCode} - ${
          selected.brandGenType == "G" ? "Generic" : "Brand"
        } - ${selected.medForm} - ${selected.routeOfAdministration}`
      : CHOOSE_DEFAULT_DRUG;
    return drug;
  };

  const updatedShouldAddMoreDrug = () => {
    setShouldAddMoreDrug((prev) => !prev);
  };

  const submitDrug = (values, setSubmitting) => {
    let medDrug = [];
    medDrug.push({ ...values });

    if (medDrug[0].itemType == "both") {
      medDrug[0].itemType = "target";
      medDrug.push({ ...DRUG_VARIABLES});
      medDrug[1].itemType = "switch";
    } else if (medDrug[0].itemType == "switch") {
      medDrug[0] = { ...medDrug[0], ...DRUG_VARIABLES, itemType: "switch" };
    }
    if (medDrug[0].daysSupply === "") {
      medDrug[0].daysSupply = "30,60,90";
    }
    if (medDrug[0].faxDisplayName === "") {
      medDrug[0].faxDisplayName = drugValue.faxDisplayName;
    }
    if (medDrug[0].instructions === "") {
      medDrug[0].instructions = "Use as Directed";
    }
    if (!medDrug[0].ageLmtReqd) {
      medDrug[0].minAge = null;
      medDrug[0].maxAge = null;
    }
    for (const element of medDrug) {
      element.daysSupply = element.daysSupply.replace(/,+$/, "");
      element.daysSupply = element.daysSupply
        ? element.daysSupply.trim().replace(" ", "").replace(/,/g, ";")
        : element.daysSupply;
      element.drugDsoId = selected.drugDsoId;
    }
    setSubmitting(true);
    sendAuthenticatedAsyncRequest(
      `/cat/codex-version/${workingCodexVersionId}/dynamic-switch-groups/${dynamicSwitchGroup.dynamicSwitchGroupId}/add-drugs`,
      "POST",
      medDrug,
      (r) => {
        setSubmitting(false);
        if (values.offMarketStrengthsExist) {
          showNotification({
            title: "Warning!",
            message: "The added drug contains some off-market strengths",
            position: "tr",
            type: "warning",
          });
        }
        showNotification({
          title: "New Drug added successfully!",
          position: "tr",
          type: "success",
        });
        if (shouldAddMoreDrug) {
          const reset = returnInitailState();
          setSelected(reset.selected);
          setDrugValue(reset.drugValue);
          setIsLoadingValidCases(reset.isLoadingValidCases);
          setToggleResetTableSelection(reset.toggleResetTableSelection);
        } else {
          history.goBack();
        }
      },
      (r) => {
        setSubmitting(false);
        showNotification({
          title: "Could not add the drug",
          message: "Something went wrong. Please try again later",
          position: "tr",
          type: "error",
        });
      }
    );
  };

  const addDaysSupply = (days) => {
    if (days !== "" || days === 0) {
      let next = addDays(days);
      setDrugValue((prev) => ({
        ...prev,
        daysSupply: next,
      }));
    } else {
      setDrugValue((prev) => ({
        ...prev,
        daysSupply: "",
      }));
      setDaysSupplyError(null);
    }
  };

  const drugName = getDrug();

  return (
    <Container fluid className="top-spacer">
      <Row>
        <Col md={{ span: 10, offset: 1 }}>
          <div>
            <Card variant="primary">
              <Card.Header>
                <Card.Title className="text-center">
                  <h4>{"Add New Drug"}</h4>
                </Card.Title>
              </Card.Header>
              <Card.Body className="mt-3">
                <DescriptionField
                  isEditMode={true}
                  name={"Dynamic Switch group Name"}
                  id={"dynamicSwitchGroupName"}
                  error={false}
                  savedValue={dynamicSwitchGroup?.dynamicSwitchGroupName}
                  updatedValue={dynamicSwitchGroup?.dynamicSwitchGroupName}
                  handleChange={null}
                  handleBlur={null}
                  compKey={"dynamicSwitchGroupName"}
                  key={`dynamicSwitchGroupName`}
                  isDisabled={true}
                  isHorizontalView={true}
                />
                <DescriptionField
                  isEditMode={true}
                  name={"Drug To Add"}
                  id={"drugToAdd"}
                  error={false}
                  savedValue={CHOOSE_DEFAULT_DRUG}
                  updatedValue={drugName}
                  handleChange={null}
                  handleBlur={null}
                  compKey={"drugToAdd"}
                  key={`drugToAdd`}
                  isDisabled={true}
                  isHorizontalView={true}
                />
                {drugName != CHOOSE_DEFAULT_DRUG && isLoadingValidCases ? (
                  <Spinner />
                ) : null}
                <Formik
                  enableReinitialize={true}
                  initialValues={drugValue}
                  onSubmit={(values, { setSubmitting }) => {
                    submitDrug(values, setSubmitting);
                  }}
                  validationSchema={DRUG_OPTION_SCHEMA}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="mb-2">
                        <NewDrugRestrictedItemTypeField
                          drugDsoId={selected.drugDsoId}
                          savedValue={drugValue.itemType}
                          updatedValue={values.itemType}
                          values={values}
                          handleBlur={handleBlur}
                          setIsLoadingValidCases={setIsLoadingValidCases}
                          isLoadingValidCases={isLoadingValidCases}
                          isVisible={
                            drugName != CHOOSE_DEFAULT_DRUG &&
                            !isLoadingValidCases
                          }
                        />
                      </div>
                      {drugName != CHOOSE_DEFAULT_DRUG &&
                      !isLoadingValidCases &&
                      (values.itemType == "target" ||
                        values.itemType == "both") ? (
                        <>
                          <div className="mb-2">
                            <DescriptionField
                              isEditMode={true}
                              name={"Priority"}
                              id={"priority"}
                              error={
                                errors.priority &&
                                touched.priority &&
                                errors.priority
                              }
                              savedValue={
                                drugValue.priority && drugValue.priority != 999
                                  ? drugValue.priority
                                  : "-"
                              }
                              updatedValue={values.priority}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              compKey={"drug-priority"}
                              controlProps={{
                                type: "number",
                              }}
                              key={`drug-option-priority-${drugValue.drugDsoId}`}
                              isHorizontalView={true}
                            />
                          </div>
                          <div className="mb-2">
                            <DescriptionField
                              isEditMode={true}
                              name={"Display Name"}
                              id={"faxDisplayName"}
                              error={
                                errors.faxDisplayName &&
                                touched.faxDisplayName &&
                                errors.faxDisplayName
                              }
                              savedValue={drugValue.faxDisplayName}
                              updatedValue={values.faxDisplayName}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              compKey={"faxDisplayName"}
                              key={`faxDisplayName`}
                              isHorizontalView={true}
                            />
                          </div>
                          <div className="mb-2">
                            <DescriptionField
                              isEditMode={true}
                              name={"Instructions"}
                              id={"instructions"}
                              error={errors.instructions}
                              savedValue={drugValue.instructions}
                              updatedValue={values.instructions}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              compKey={"instructions"}
                              key={`instructions`}
                              isHorizontalView={true}
                              placeholder={"Use as Directed"}
                            />
                          </div>
                          <AgeFields
                            handleChange={handleChange}
                            values={values}
                            drugValue={drugValue}
                            handleBlur={handleBlur}
                            setAgeError={setAgeError}
                            ageError={ageError}
                          />
                          <div className="mb-2 days-supply row">
                            <Col md={3}>
                              Days Supply (Add comma separated values)
                            </Col>
                            <Col className="days-supply-field" md={9}>
                              <TagField
                                value={drugValue.daysSupply}
                                error={daysSupplyError}
                                onChange={addDaysSupply}
                              />
                            </Col>
                          </div>
                        </>
                      ) : null}
                      <Row className="align-items-md-center bottom-spacer top-spacer">
                        <Col md={7}>
                          <CheckField
                            key={`shouldAddMoreDrug`}
                            shouldDisplayTitle={false}
                            compKey={"should-add-more-drug"}
                            id={"shouldAddMoreDrug"}
                            title={""}
                            isEditMode={true}
                            label={"Add more Drugs"}
                            handleBlur={() => {}}
                            handleChange={updatedShouldAddMoreDrug}
                            value={shouldAddMoreDrug}
                          />
                        </Col>
                        <Col md={2}>
                          <Button
                            variant="outline-dark"
                            block
                            onClick={() => history.goBack()}
                            disabled={isSubmitting}
                            className="btn-block"
                          >
                            Cancel
                          </Button>
                        </Col>
                        <Col md={3}>
                          <Button
                            className="btn-block"
                            type="submit"
                            variant="success"
                            block
                            disabled={
                              drugName == CHOOSE_DEFAULT_DRUG ||
                              isLoadingValidCases ||
                              !values.itemType ||
                              isSubmitting ||
                              daysSupplyError ||
                              ageError
                            }
                          >
                            {isSubmitting ? "Adding Drug... " : "Add Drug"}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </Card.Body>
              <AllDrugList
                showResetListButton
                placedOnLightBg
                isSelectRow={true}
                selected={selected}
                onSelect={onSelect}
                resetTableSelection={toggleResetTableSelection}
              />
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
};
export default withRouter(NewDrug);
